import React, { useEffect, useState } from "react";
import { Box, Typography, Tooltip, IconButton } from "@material-ui/core";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import MarkdownEditorWithPreview from "../../../components/MarkdownEditor/MarkdownEditorWithPreview";

export default function OutlineEditor({
  moduleInformation,
  setModuleInformation,
  activeStatusDataValue,
}) {
  const [outlineError, setOutlineError] = useState(null);

  // Run validation when content or status changes
  useEffect(() => {
    if (moduleInformation !== undefined && moduleInformation !== null) {
      validateOutlineMarkdown(moduleInformation);
    }
  }, [moduleInformation, activeStatusDataValue]);

  const validateOutlineMarkdown = (markdown) => {
    // Only validate when activeStatusDataValue === 1
    if (activeStatusDataValue !== 1) {
      setOutlineError(null);
      return;
    }

    setOutlineError(null);
    if (!markdown || !markdown.trim()) {
      setOutlineError("Markdown outline cannot be empty.");
      return;
    }

    const lines = markdown.replace(/\r\n/g, "\n").split("\n");
    const sectionRegex = /^#\s+.+$/;
    const slideRegex = /^##\s+.+$/;

    let hasSection = false;
    let hasSlide = false;
    let currentSectionLine = null;
    let sectionHasSlide = false;

    for (let index = 0; index < lines.length; index++) {
      const rawLine = lines[index];
      const lineNumber = index + 1;
      const line = rawLine.trimEnd();
      if (!line.trim()) continue;

      if (sectionRegex.test(line)) {
        if (currentSectionLine !== null && !sectionHasSlide) {
          setOutlineError(
            `Section starting on line ${currentSectionLine} has no level-2 headings (## ...).`
          );
        }
        hasSection = true;
        currentSectionLine = lineNumber;
        sectionHasSlide = false;
        continue;
      }

      if (slideRegex.test(line)) {
        if (currentSectionLine === null) {
          setOutlineError(
            `Level-2 heading before any section on line ${lineNumber}: "${line}". Start with a "# Section title" first.`
          );
        } else {
          hasSlide = true;
          sectionHasSlide = true;
        }
        continue;
      }

      if (line.startsWith("#")) {
        setOutlineError(
          `Invalid heading on line ${lineNumber}: "${line}". Only "# Section title" and "## Heading title" are allowed.`
        );
        continue;
      }

      if (currentSectionLine === null) {
        setOutlineError(
          `Text before any section on line ${lineNumber}: "${line}". Add a "# Section title" first.`
        );
        continue;
      }

      if (!sectionHasSlide) {
        setOutlineError(
          `Text must come after a level-2 heading. Found on line ${lineNumber}: "${line}". Add a "## Heading title" first in this section (started on line ${currentSectionLine}).`
        );
        continue;
      }
    }

    if (currentSectionLine !== null && !sectionHasSlide) {
      setOutlineError(
        `Section starting on line ${currentSectionLine} has no level-2 headings (## ...).`
      );
    }
    if (!hasSection) {
      setOutlineError(
        'At least one level-1 section is required (e.g. "# Section 1").'
      );
    }
    if (!hasSlide) {
      setOutlineError(
        'At least one level-2 heading is required (e.g. "## Heading 1" inside a section).'
      );
    }
  };

  return (
    <>
      {activeStatusDataValue === 1 && (
        <>
          <Box
            style={{
              marginTop: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
            }}
          >
            <Typography variant="h5" style={{ margin: 0 }}>
              Outline
            </Typography>
            <Tooltip
              arrow
              placement="left"
              title={
                <Box>
                  <Typography
                    variant="body2"
                    gutterBottom
                    style={{ fontWeight: 600 }}
                  >
                    Expected Outline Structure
                  </Typography>
                  <Typography
                    variant="caption"
                    component="div"
                    style={{ whiteSpace: "pre-line" }}
                  >
                    {`# Section 1\n## Slide 1 header\n- Point 1\n- Point 2\n## Slide 2 header\n- Point 1\n- Point 2\n# Section 2\n... and so on`}
                  </Typography>
                  <Typography
                    variant="caption"
                    component="div"
                    style={{ marginTop: 8 }}
                  >
                    Use level-1 headings (#) for sections, level-2 headings
                    (##) for individual slides, and bullet points for slide
                    details.
                  </Typography>
                </Box>
              }
            >
              <IconButton size="small" aria-label="outline format help">
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="body1">
            Validate the outline structure and content for slide generation.
          </Typography>
        </>
      )}

      <div data-color-mode="light" style={{ marginTop: "20px" }}>
        <MarkdownEditorWithPreview
          value={moduleInformation}
          onChange={(content) => {
            setModuleInformation(content);
            validateOutlineMarkdown(content);
          }}
          error={outlineError}
        />
      </div>
    </>
  );
}

export { OutlineEditor };
